/**
 * ExpertTermsBanner — asks an existing expert, once, to accept the current Expert Terms
 * (agent data responsibility). New experts accept them in the onboarding form; this covers
 * accounts created before the terms existed and every later version bump.
 *
 * Non-blocking by design: a small card pinned to the bottom of the screen, the dashboard stays
 * fully usable. It has no close button — it disappears only when the terms are accepted — and it
 * can be collapsed to a slim bar so it never covers work.
 *
 * Server: GET /onboarding/terms/accept → { version, title, text, needsAcceptance, canAccept }
 *         POST /onboarding/terms/accept { version }
 * Team members cannot accept for the account holder (canAccept=false), so they never see it.
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';

const Wrap = styled.div`
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  width: min(640px, calc(100vw - 32px));
  z-index: 1500;
  background: #161b22;
  border: 1px solid rgba(210, 153, 34, 0.55);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  color: #e6edf3;
  font-size: 14px;
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
`;
const Body = styled.div`
  padding: 0 16px 14px 16px;
`;
const Text = styled.div`
  max-height: 180px;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 13px;
  color: rgba(230, 237, 243, 0.85);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 10px 12px;
`;
const Btn = styled.button`
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Link = styled.button`
  background: none;
  border: none;
  color: #8b949e;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  white-space: nowrap;
`;

export default function ExpertTermsBanner() {
  const { isAuthenticated } = useAuth0();
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [status, setStatus] = useState(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    api.get('onboarding/terms/accept')
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { /* non-blocking: if the check fails we simply do not ask now */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!status || !status.needsAcceptance || !status.canAccept) return null;

  const accept = async () => {
    setSaving(true);
    try {
      const r = await api.post('onboarding/terms/accept', { version: status.version });
      setStatus(r.data);
      showToast('Thank you — the Expert Terms are accepted.', 'success');
    } catch (e) {
      const data = e?.response?.data;
      if (data?.terms) { setStatus({ ...status, ...data.terms }); setChecked(false); }
      showToast(data?.message || 'Could not save your acceptance. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changed = !!status.accepted;
  return (
    <Wrap role="region" aria-label="Expert Terms">
      <Head>
        <strong>{changed ? 'The Expert Terms have changed' : 'Please accept the Expert Terms'}</strong>
        <Link type="button" onClick={() => setCollapsed((c) => !c)}>{collapsed ? 'Show' : 'Later'}</Link>
      </Head>
      {!collapsed && (
        <Body>
          <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 8 }}>
            {changed
              ? `You accepted version ${status.accepted.version}. Please read version ${status.version} and accept it.`
              : 'Your account includes your own AI agent. Please read and accept these terms once.'}
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{status.title}</div>
          <Text>{status.text}</Text>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
              <span>I have read and accept these terms (version {status.version}).</span>
            </label>
            <Btn type="button" onClick={accept} disabled={!checked || saving}>{saving ? 'Saving…' : 'Accept'}</Btn>
          </div>
        </Body>
      )}
    </Wrap>
  );
}
